import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark } from 'lucide-react';
import { formatClock } from '@mlm/shared';
import { Button, ConfirmDialog, SegmentedControl, Textarea } from '@mlm/ui';
import {
  createLessonNote,
  deleteLessonNote,
  listCourseNotes,
  updateLessonNote,
  type LessonNote,
} from '../lib/api';

type Scope = 'lesson' | 'all';

/**
 * Private notes and bookmarks, timestamped to the playhead.
 *
 * A bookmark is a note with an empty body — one row, one table, no second
 * concept. Timestamps are the point: tapping one seeks the player, and a chip
 * belonging to another lesson navigates there first.
 */
export function LessonNotes({
  courseId,
  lessonId,
  lessonTitles,
  getCurrentTime,
  onSeek,
}: {
  courseId: string;
  lessonId: string;
  lessonTitles: Map<string, string>;
  getCurrentTime: () => number | null;
  onSeek: (seconds: number, lessonId: string) => void;
}) {
  const qc = useQueryClient();
  const [scope, setScope] = useState<Scope>('lesson');
  const [draft, setDraft] = useState('');
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<LessonNote | null>(null);

  const key = ['lesson-notes', courseId, scope === 'lesson' ? lessonId : 'all'];
  const q = useQuery({
    queryKey: key,
    queryFn: () => listCourseNotes(courseId, scope === 'lesson' ? { lessonId } : {}),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['lesson-notes', courseId] });
  const at = getCurrentTime();

  const add = useMutation({
    meta: { errorMessage: "Couldn't save your note — please try again." },
    mutationFn: () =>
      createLessonNote(courseId, lessonId, {
        positionSeconds: at != null ? Math.floor(at) : null,
        body: draft.trim(),
      }),
    onSuccess: () => {
      setDraft('');
      setComposing(false);
      void invalidate();
    },
  });
  const save = useMutation({
    meta: { errorMessage: "Couldn't update your note — please try again." },
    mutationFn: () => updateLessonNote(courseId, editing!.id, editing!.body.trim()),
    onSuccess: () => {
      setEditing(null);
      void invalidate();
    },
  });
  const remove = useMutation({
    meta: { errorMessage: "Couldn't delete that note — please try again." },
    mutationFn: (note: LessonNote) => deleteLessonNote(courseId, note.id),
    onSuccess: () => {
      setConfirmDelete(null);
      void invalidate();
    },
  });

  const notes = q.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button size="sm" variant="outline" disabled={composing} onClick={() => setComposing(true)}>
          {at != null ? `Add note at ${formatClock(at)}` : 'Add note'}
        </Button>
        <SegmentedControl<Scope>
          size="sm"
          ariaLabel="Which notes to show"
          value={scope}
          onChange={setScope}
          options={[
            { value: 'lesson', label: 'This lesson' },
            { value: 'all', label: 'All lessons' },
          ]}
        />
      </div>

      {composing && (
        <div className="space-y-2">
          <Textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="What do you want to remember about this moment?"
            rows={3}
            maxLength={4000}
          />
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setComposing(false);
                setDraft('');
              }}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={() => add.mutate()} disabled={add.isPending}>
              Save note
            </Button>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        // Never the empty state on failure — "no notes yet" would be a lie.
        <p className="text-sm text-muted-foreground">
          Your notes couldn&apos;t be loaded.{' '}
          <button onClick={() => q.refetch()} className="font-medium text-primary hover:underline">
            Retry
          </button>
        </p>
      ) : notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No notes yet. Press B to bookmark this moment, or add a note.
        </p>
      ) : (
        <ul className="space-y-2">
          {notes.map((note) => {
            const isEditing = editing?.id === note.id;
            const otherLesson = note.lessonId !== lessonId;
            return (
              <li key={note.id} className="rounded-lg border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  {note.positionSeconds != null && (
                    <button
                      onClick={() => onSeek(note.positionSeconds as number, note.lessonId)}
                      className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium tabular-nums text-foreground hover:bg-accent"
                      title={
                        otherLesson ? 'Go to that lesson and play from here' : 'Play from here'
                      }
                    >
                      {formatClock(note.positionSeconds)}
                    </button>
                  )}
                  {!note.body && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Bookmark className="h-3 w-3" /> Bookmark
                    </span>
                  )}
                  {otherLesson && scope === 'all' && (
                    <span className="truncate text-xs text-muted-foreground">
                      {lessonTitles.get(note.lessonId) ?? 'Another lesson'}
                    </span>
                  )}
                  <div className="ml-auto flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setEditing(isEditing ? null : { id: note.id, body: note.body })
                      }
                    >
                      {isEditing ? 'Cancel' : note.body ? 'Edit' : 'Add a note'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(note)}>
                      Delete
                    </Button>
                  </div>
                </div>
                {isEditing ? (
                  <div className="mt-2 space-y-2">
                    <Textarea
                      autoFocus
                      value={editing.body}
                      onChange={(e) => setEditing({ id: note.id, body: e.target.value })}
                      rows={3}
                      maxLength={4000}
                    />
                    <div className="flex justify-end">
                      <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  note.body && <p className="mt-2 whitespace-pre-wrap text-sm">{note.body}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title="Delete this note?"
        description="This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        onConfirm={() => {
          if (confirmDelete) remove.mutate(confirmDelete);
        }}
      />
    </div>
  );
}
